export const CHAT_GUEST_AUTH_CMD = "customerChatGuestAuth";
export const ANONYMOUS_CHAT_CACHE_KEY = "customer_chat_cache_anonymous";

const CHAT_INSTALL_ID_KEY = "chat_install_id";
const CHAT_TOKEN_KEY = "chat_token";
const CHAT_TOKEN_EXPIRES_AT_KEY = "chat_token_expires_at";
const ANONYMOUS_CHAT_ACTIVE_KEY = "anonymous_chat_active";
const LAST_MSG_ID_KEY = "last_msg_id";
const TOKEN_REFRESH_EARLY_SECONDS = 5 * 60;
const CHAT_INSTALL_ID_PATTERN = /^[0-9a-f]{64}$/;

let guestAuthPromise = null;

const getStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const readStorage = (key) => {
  const storage = getStorage();
  if (!storage) {
    return "";
  }
  try {
    return storage.getItem(key) || "";
  } catch (error) {
    return "";
  }
};

const writeStorage = (key, value) => {
  const storage = getStorage();
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(key, String(value));
    return true;
  } catch (error) {
    return false;
  }
};

const removeStorage = (key) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch (error) {
    // Persistence may be unavailable in restricted browser modes.
  }
};

const parseNonNegativeInteger = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return 0;
  }
  return Math.floor(parsedValue);
};

const parseCustomerChatResponse = (res) => {
  if (!res) {
    return { ok: false, data: {}, error: "Request failed" };
  }
  if (res.status === false) {
    return {
      ok: false,
      data: {},
      error: res.errdesc || "Request failed",
      code: res.errdcode,
    };
  }
  let data = res.status === true ? res.data || {} : res.data || res.msg || res;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (error) {
      // Keep compatibility with non-JSON response data.
    }
  }
  return { ok: true, data };
};

export const generateChatInstallId = () => {
  const cryptoProvider =
    typeof window !== "undefined" && (window.crypto || window.msCrypto);
  if (!cryptoProvider || typeof cryptoProvider.getRandomValues !== "function") {
    throw new Error("Secure random number generator is unavailable");
  }

  const randomBytes = new Uint8Array(32);
  cryptoProvider.getRandomValues(randomBytes);
  return Array.from(randomBytes)
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
};

export const getOrCreateChatInstallId = () => {
  const storedId = readStorage(CHAT_INSTALL_ID_KEY).trim().toLowerCase();
  if (CHAT_INSTALL_ID_PATTERN.test(storedId)) {
    return storedId;
  }

  const generatedId = generateChatInstallId();
  if (!writeStorage(CHAT_INSTALL_ID_KEY, generatedId)) {
    throw new Error("Anonymous chat identity cannot be persisted");
  }
  return generatedId;
};

export const getAnonymousChatState = () => ({
  chatInstallId: readStorage(CHAT_INSTALL_ID_KEY),
  chatToken: readStorage(CHAT_TOKEN_KEY),
  expiresAt: parseNonNegativeInteger(readStorage(CHAT_TOKEN_EXPIRES_AT_KEY)),
  active: readStorage(ANONYMOUS_CHAT_ACTIVE_KEY) === "true",
  lastMsgId: parseNonNegativeInteger(readStorage(LAST_MSG_ID_KEY)),
});

export const setAnonymousChatActive = (active) => {
  writeStorage(ANONYMOUS_CHAT_ACTIVE_KEY, active ? "true" : "false");
};

export const setAnonymousChatLastMsgId = (lastMsgId) => {
  const normalizedLastMsgId = parseNonNegativeInteger(lastMsgId);
  if (!normalizedLastMsgId) {
    removeStorage(LAST_MSG_ID_KEY);
    return;
  }
  writeStorage(LAST_MSG_ID_KEY, normalizedLastMsgId);
};

export const closeAnonymousChatSession = () => {
  setAnonymousChatActive(false);
  setAnonymousChatLastMsgId(0);
};

const storeGuestCredentials = (chatToken, expiresAt) => {
  const normalizedExpiresAt = parseNonNegativeInteger(expiresAt);
  if (!chatToken || !normalizedExpiresAt) {
    return false;
  }
  return (
    writeStorage(CHAT_TOKEN_KEY, chatToken) &&
    writeStorage(CHAT_TOKEN_EXPIRES_AT_KEY, normalizedExpiresAt)
  );
};

const hasUsableGuestToken = (state, nowSeconds) =>
  Boolean(
    state.chatToken &&
      state.expiresAt &&
      state.expiresAt > nowSeconds + TOKEN_REFRESH_EARLY_SECONDS
  );

export const authenticateCustomerChatGuest = async (sendCmd) => {
  if (typeof sendCmd !== "function") {
    throw new Error("Customer chat request function is unavailable");
  }
  if (guestAuthPromise) {
    return guestAuthPromise;
  }

  guestAuthPromise = (async () => {
    let chatInstallId;
    try {
      chatInstallId = getOrCreateChatInstallId();
    } catch (error) {
      return { ok: false, data: {}, error: error.message || "Anonymous chat is unavailable" };
    }

    let response;
    try {
      response = parseCustomerChatResponse(
        await sendCmd(CHAT_GUEST_AUTH_CMD, { chat_install_id: chatInstallId })
      );
    } catch (error) {
      return { ok: false, data: {}, error: "Anonymous chat authentication failed" };
    }
    if (!response.ok) {
      return response;
    }

    const chatToken = response.data && response.data.chat_token;
    const expiresAt = response.data && response.data.expires_at;
    if (!storeGuestCredentials(chatToken, expiresAt)) {
      return { ok: false, data: {}, error: "Anonymous chat credentials cannot be saved" };
    }
    return response;
  })();

  try {
    return await guestAuthPromise;
  } finally {
    guestAuthPromise = null;
  }
};

export const ensureCustomerChatGuestToken = async ({
  forceRefresh = false,
  nowSeconds = Math.floor(Date.now() / 1000),
  sendCmd,
} = {}) => {
  const state = getAnonymousChatState();
  if (!forceRefresh && hasUsableGuestToken(state, nowSeconds)) {
    return { ok: true, data: { chat_token: state.chatToken, expires_at: state.expiresAt } };
  }
  return authenticateCustomerChatGuest(sendCmd);
};

const isAnonymousTokenError = (code, cmd) =>
  typeof code === "string" && (code === `${cmd}_13` || code.endsWith("_13"));

const withoutIdentityFields = (payload) => {
  const requestPayload = { ...(payload || {}) };
  delete requestPayload.token;
  delete requestPayload.chat_token;
  return requestPayload;
};

export const sendCustomerChatRequest = async ({
  cmd,
  payload,
  playerToken,
  useAnonymous,
  sendCmd,
}) => {
  if (typeof sendCmd !== "function") {
    throw new Error("Customer chat request function is unavailable");
  }
  const requestPayload = withoutIdentityFields(payload);
  if (!useAnonymous) {
    return parseCustomerChatResponse(
      await sendCmd(cmd, { ...requestPayload, token: playerToken })
    );
  }

  let authResponse = await ensureCustomerChatGuestToken({ sendCmd });
  if (!authResponse.ok) {
    return { ...authResponse, stopAutomaticRetry: true };
  }

  let response = parseCustomerChatResponse(
    await sendCmd(cmd, {
      ...requestPayload,
      chat_token: authResponse.data.chat_token,
    })
  );
  if (!response.ok && isAnonymousTokenError(response.code, cmd)) {
    authResponse = await ensureCustomerChatGuestToken({ forceRefresh: true, sendCmd });
    if (!authResponse.ok) {
      return { ...authResponse, stopAutomaticRetry: true };
    }
    try {
      response = parseCustomerChatResponse(
        await sendCmd(cmd, {
          ...requestPayload,
          chat_token: authResponse.data.chat_token,
        })
      );
    } catch (error) {
      return {
        ok: false,
        data: {},
        error: "Request failed",
        stopAutomaticRetry: true,
      };
    }
    if (!response.ok) {
      return { ...response, stopAutomaticRetry: true };
    }
  }
  return response;
};
